import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Badge,
  Avatar,
  Divider,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inbox as InboxIcon,
  Notifications as MentionsIcon,
  AssignmentLate as UnassignedIcon,
  FactCheck as ClosedIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const MainNavigation = ({ 
  activeSection,
  onSectionChange,
  unreadCount,
  onlineUsers,
  users,
  theme,
  isMobile
}) => {
  // Navigation sections - add dashboard to top of the list
  const sections = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
    { id: 'inbox', label: 'Inbox', icon: InboxIcon, badge: unreadCount.inbox },
    { id: 'mentions', label: 'Mentions', icon: MentionsIcon, badge: unreadCount.mentions },
    { id: 'unassigned', label: 'Unassigned', icon: UnassignedIcon, badge: unreadCount.unassigned },
    { id: 'closed', label: 'Closed', icon: ClosedIcon }
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: '100%', sm: 240 },
        height: '100%',
        background: 'rgba(22, 28, 36, 0.8)',
        backdropFilter: 'blur(10px)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Main navigation list */}
      <List sx={{ pt: 2, flex: 1 }}>
        {sections.map((section) => (
          <ListItem
            button
            key={section.id}
            selected={activeSection === section.id}
            onClick={() => onSectionChange(section.id)}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              color: 'white',
              '&.Mui-selected': {
                backgroundColor: 'rgba(76, 133, 240, 0.15)',
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(76, 133, 240, 0.25)'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <ListItemIcon
              sx={{
                color: activeSection === section.id
                  ? theme.palette.primary.main
                  : 'rgba(255, 255, 255, 0.7)',
                minWidth: '36px'
              }}
            >
              {section.badge ? (
                <Badge
                  color="error"
                  badgeContent={section.badge}
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.6rem',
                      height: '16px',
                      minWidth: '16px'
                    }
                  }}
                >
                  <section.icon fontSize="small" />
                </Badge>
              ) : (
                <section.icon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{
                fontWeight: activeSection === section.id ? 'medium' : 'normal'
              }}
            />
          </ListItem>
        ))}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }}></Box>

        {/* Settings section */}
        <ListItem
          button
          sx={{
            borderRadius: '8px',
            mx: 1,
            mb: 0.5,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          <ListItemIcon
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              minWidth: '36px'
            }}
          >
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mx: 2 }} />

      {/* Team/Online members section */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">
            TEAM MEMBERS
          </Typography>
          <Tooltip title="Add team member">
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {users.slice(0, 5).map((user) => (
            <Tooltip key={user.id} title={`${user.name} ${onlineUsers.includes(user.id) ? '(Online)' : '(Offline)'}`}>
              <Avatar
                src={user.avatar}
                alt={user.name}
                sx={{
                  width: 32,
                  height: 32,
                  border: onlineUsers.includes(user.id)
                    ? `2px solid ${theme.palette.success.main}`
                    : '2px solid transparent',
                  opacity: onlineUsers.includes(user.id) ? 1 : 0.6,
                  transition: 'all 0.2s ease'
                }}
              >
                {user.name.charAt(0)}
              </Avatar>
            </Tooltip>
          ))}
          {users.length > 5 && (
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'rgba(76, 133, 240, 0.2)',
                color: theme.palette.primary.main,
                fontSize: '0.75rem'
              }}
            >
              +{users.length - 5}
            </Avatar>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default MainNavigation;
